import { cn } from "@postdaily/ui/lib/utils";

import {
  type PlatformId,
  PROVIDER_BRAND,
  ProviderMarkSvg,
  type ProviderVisual,
} from "@/components/social-accounts/provider-icon";

/**
 * A brand logo on its coloured tile — the design uses these at many bespoke
 * sizes/radii (channel showcase, the hero product mock, the queue rows), so
 * unlike the fixed-size `ProviderIcon` this takes free-form classes. Both share
 * the same `PROVIDER_BRAND` source, so the SVG paths live in exactly one place.
 */
export function BrandMark({
  platform,
  className,
  iconClassName,
}: {
  platform: PlatformId;
  /** Sizing/radius of the coloured tile. */
  className?: string;
  /** Sizing of the white glyph inside. */
  iconClassName?: string;
}) {
  // Widened: see the note in provider-icon.tsx about `satisfies`.
  const { bg, path, mark }: ProviderVisual = PROVIDER_BRAND[platform];
  if (mark) {
    /*
      A provider that ships its own artwork gets drawn, not rebuilt: the tile
      would restate the mark's container at the tile's aspect ratio, and for a
      1.43:1 mark in a square tile that is the "squash" YouTube forbids. The
      span keeps the caller's box so surrounding layout is unchanged; the mark
      sits inside it at full width.
    */
    return (
      <span
        className={cn(
          "inline-flex shrink-0 items-center justify-center",
          className,
        )}
      >
        <ProviderMarkSvg mark={mark} className="h-[70%]" />
      </span>
    );
  }
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center text-white",
        className,
      )}
      style={{ backgroundColor: bg }}
    >
      <svg
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
        className={cn("size-[18px]", iconClassName)}
      >
        <path d={path} />
      </svg>
    </span>
  );
}

/**
 * Monochrome brand glyph (no tile) — inherits `currentColor`, used for the
 * footer's flat social links that tint on hover.
 */
export function BrandGlyph({
  platform,
  className,
}: {
  platform: PlatformId;
  className?: string;
}) {
  const { path } = PROVIDER_BRAND[platform];
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className={cn("size-4", className)}
    >
      <path d={path} />
    </svg>
  );
}
